// App-wide error boundary (audit ERR-01). A render error anywhere below this
// boundary used to white-screen the whole SPA with no recovery; now it shows a
// fallback with Reload / Go-home actions. Class component because React error
// boundaries must be classes. Intentionally dependency-light (no i18n/context
// inside the fallback) so it still renders when app context is what broke.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional smaller inline fallback (e.g. per-page vs full-screen). */
  variant?: 'screen' | 'inline';
  /** Label for the boundary in logs (which region failed). */
  label?: string;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console for now; wire to real error tracking (Sentry/OTel) in a later phase.
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const inline = this.props.variant === 'inline';
    return (
      <div
        role="alert"
        className={
          inline
            ? 'tk-card m-4 flex flex-col items-center gap-3 p-6 text-center'
            : 'flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center'
        }
      >
        <div className="text-lg font-semibold">Something went wrong</div>
        <p className="max-w-md text-sm text-muted">
          An unexpected error occurred. You can try again, or return to the dashboard.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {inline ? (
            <button className="tk-btn-primary" onClick={this.reset}>Try again</button>
          ) : (
            <>
              <button className="tk-btn-primary" onClick={() => window.location.reload()}>Reload</button>
              <button className="tk-btn-ghost" onClick={() => { window.location.href = '/'; }}>Go to dashboard</button>
            </>
          )}
        </div>
      </div>
    );
  }
}
